import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { Scalars } from 'types/graphql'
import { media, phoneMaxWidth } from 'utils/media'
import { getSegmentTrackingId } from 'utils/segmentCookie'
import { outboundLinkUrl, urlWithHost } from 'utils/urlWithHost'
import { ArrowLink } from 'lib/ArrowLink'
import { LinkButton } from 'lib/Button/Button'
import { FloatingPopover, useFloatingPopoverController } from 'lib/FloatingPopover'
import { Link } from 'lib/Link'
import { Text } from 'lib/Text'

const Box = styled.div`
  align-items: flex-start;
  align-self: stretch;
  background-color: white;
  border-radius: 8px;
  cursor: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  padding: 24px;
  text-align: left;
  ${media.phoneDown} {
    padding: 0;
  }
`

const ContinueButton = styled(LinkButton)`
  align-self: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const formatLabel = (label: string) => {
  try {
    return new URL(label).hostname
  } catch {
    return label
  }
}

interface TrackingProps {
  brandId: Scalars['ID']
  token?: string | null
  anonymousId?: string | null
  ref?: string
}

const getOutboundTrackingUrl = ({ brandId, token, anonymousId, ref }: TrackingProps) => {
  const url = new URL(outboundLinkUrl(`/brands/${brandId.toString()}`))

  if (anonymousId) {
    url.searchParams.set('aid', anonymousId)
  } else {
    url.searchParams.delete('aid')
  }

  if (token) {
    url.searchParams.set('t', token)
  } else {
    url.searchParams.delete('t')
  }

  if (ref) {
    url.searchParams.set('ref', ref)
  } else {
    url.searchParams.delete('ref')
  }

  return url.href
}

interface ContentProps {
  label: string
  href: string
  onButtonClick: () => void
}

const PopoverContent = ({ href, label, onButtonClick }: ContentProps) => {
  return (
    <Box>
      <Text weight="semi">Buy better, not more</Text>
      <Text as="p" size="pxs" color="neutral-60">
        Things you buy through this link may earn us a commission that we use to fight problems with online reviews.{' '}
        <Link href="/about">Learn more</Link>.
      </Text>
      <ContinueButton onClick={onButtonClick} href={href} target="_blank">
        Continue to<u>{label}</u>
      </ContinueButton>
    </Box>
  )
}

interface Props {
  brandId: Scalars['ID']
  label: string
}
export const BrandWebUrl = ({ brandId, label }: Props) => {
  const { asPath } = useRouter()
  const [token, setToken] = useState<string | null>(null)
  const [anonymousId, setAnonymousId] = useState<string | null>(getSegmentTrackingId())
  const url = getOutboundTrackingUrl({ brandId, token, anonymousId, ref: urlWithHost(asPath) })
  const controls = useFloatingPopoverController()
  const isMobile = useMedia(`(max-width: ${phoneMaxWidth}px)`, false)

  useEffect(() => {
    const auth = getAuth()

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then((token) => {
          setToken(token)
        })
      } else {
        setToken(null)
      }
    })

    window.analytics.ready(() => {
      if (!anonymousId) {
        try {
          setAnonymousId(window.analytics.user().anonymousId())
        } catch {}
      }
    })

    return unsubscribe
  }, [anonymousId])

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isMobile) {
      controls.setOpen(!controls.open)
      e.stopPropagation()
      e.preventDefault()

      if (controls.open) {
        window.analytics.track('Affiliate Prompt Opened', {
          brandId,
          label: formatLabel(label),
          url: url
        })
      }
    }
  }

  const onButtonClick = () => {
    window.analytics.track('Affiliate Prompt Followed', {
      brandId,
      label: formatLabel(label),
      url: url
    })
  }

  return (
    <FloatingPopover
      arrow
      placement="bottom-start"
      controls={controls}
      content={<PopoverContent onButtonClick={onButtonClick} label={formatLabel(label)} href={url} />}>
      <ArrowLink onClick={onLinkClick} label={formatLabel(label)} url={url} underlined isExternalUrl />
    </FloatingPopover>
  )
}
